import { useEffect, useRef, useState } from "preact/hooks";
import { apiFetch, apiJson } from "../api";
import type { ShortUrl, Tenant } from "../api";

interface Props {
  tenant: Tenant;
}

export function Urls({ tenant }: Props) {
  const [urls, setUrls] = useState<ShortUrl[]>([]);
  const [filter, setFilter] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<ShortUrl | null>(null);
  const [bulkResult, setBulkResult] = useState<unknown>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  async function load() {
    try {
      const qs = filter ? `?keyword=${encodeURIComponent(filter)}` : "";
      const data = await apiJson<{ urls: ShortUrl[] }>(
        `/api/tenants/${tenant.code}/urls${qs}`,
      );
      setUrls(data.urls);
      setErr(null);
    } catch (e) {
      setErr(String(e));
    }
  }

  useEffect(() => {
    load();
  }, [tenant.code]);

  async function onBulk(e: Event) {
    const input = e.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    const text = await file.text();
    try {
      const res = await apiFetch(`/api/tenants/${tenant.code}/bulk`, {
        method: "POST",
        body: text,
        headers: { "content-type": "text/csv" },
      });
      setBulkResult(await res.json());
      await load();
    } catch (e) {
      setErr(String(e));
    } finally {
      input.value = "";
    }
  }

  return (
    <section>
      <header class="row">
        <h2>URLs — {tenant.code}</h2>
        <input
          placeholder="filter by keyword"
          value={filter}
          onInput={(e) => setFilter((e.target as HTMLInputElement).value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") load();
          }}
        />
        <button onClick={load}>Reload</button>
        <button onClick={() => setCreating(true)}>+ URL</button>
        <button onClick={() => fileRef.current?.click()}>Bulk CSV</button>
        <input
          type="file"
          accept=".csv,text/csv"
          ref={fileRef}
          onChange={onBulk}
          style={{ display: "none" }}
        />
        <button
          onClick={async () => {
            if (!confirm("Rebuild KV from D1?")) return;
            const r = await apiJson<{ removed: number; written: number }>(
              `/api/tenants/${tenant.code}/resync-kv`,
              { method: "POST" },
            );
            alert(`KV resync: -${r.removed} +${r.written}`);
          }}
        >
          Resync KV
        </button>
      </header>

      {err && <div class="error">{err}</div>}
      {bulkResult != null && (
        <pre class="log">{JSON.stringify(bulkResult, null, 2)}</pre>
      )}

      <table>
        <thead>
          <tr>
            <th>Keyword</th>
            <th>Country</th>
            <th>Target URL</th>
            <th>Expires</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {urls.map((u) => (
            <tr key={u.id}>
              <td>
                <a
                  href={`https://${tenant.short_domain}/${u.keyword}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  <code>{u.keyword}</code>
                </a>
              </td>
              <td>{u.country}</td>
              <td class="truncate">
                <a href={u.target_url} target="_blank" rel="noreferrer">
                  {u.target_url}
                </a>
              </td>
              <td>
                {u.expires_at ? new Date(u.expires_at * 1000).toISOString() : "—"}
              </td>
              <td>
                <button onClick={() => setEditing(u)}>Edit</button>
                <button
                  onClick={async () => {
                    if (!confirm(`Delete ${u.keyword}/${u.country}?`)) return;
                    await apiFetch(
                      `/api/tenants/${tenant.code}/urls/${u.keyword}/${u.country}`,
                      { method: "DELETE" },
                    );
                    await load();
                  }}
                >
                  Delete
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {creating && (
        <UrlForm
          tenant={tenant}
          url={null}
          onClose={() => setCreating(false)}
          onSaved={async () => {
            setCreating(false);
            await load();
          }}
        />
      )}
      {editing && (
        <UrlForm
          tenant={tenant}
          url={editing}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await load();
          }}
        />
      )}
    </section>
  );
}

interface FormProps {
  tenant: Tenant;
  url: ShortUrl | null;
  onClose: () => void;
  onSaved: () => Promise<void>;
}

function UrlForm({ tenant, url, onClose, onSaved }: FormProps) {
  const [keyword, setKeyword] = useState(url?.keyword ?? "");
  const [country, setCountry] = useState(url?.country ?? tenant.default_language);
  const [target, setTarget] = useState(url?.target_url ?? "");
  const [expires, setExpires] = useState(
    url?.expires_at ? new Date(url.expires_at * 1000).toISOString().slice(0, 16) : "",
  );
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: Event) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      const payload = {
        keyword,
        country,
        target_url: target,
        expires_at: expires ? Math.floor(new Date(expires).getTime() / 1000) : null,
      };
      if (url) {
        await apiJson(
          `/api/tenants/${tenant.code}/urls/${url.keyword}/${url.country}`,
          { method: "PUT", body: JSON.stringify(payload) },
        );
      } else {
        await apiJson(`/api/tenants/${tenant.code}/urls`, {
          method: "POST",
          body: JSON.stringify(payload),
        });
      }
      await onSaved();
    } catch (e) {
      setErr(String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div class="modal">
      <form onSubmit={submit} class="card">
        <h3>{url ? `Edit ${url.keyword}/${url.country}` : "Create URL"}</h3>
        <label>
          Keyword
          <input
            value={keyword}
            disabled={!!url}
            onInput={(e) => setKeyword((e.target as HTMLInputElement).value)}
            required
          />
        </label>
        <label>
          Country
          <input
            value={country}
            disabled={!!url}
            onInput={(e) => setCountry((e.target as HTMLInputElement).value.toUpperCase())}
            required
          />
        </label>
        <label>
          Target URL
          <input
            value={target}
            onInput={(e) => setTarget((e.target as HTMLInputElement).value)}
            required
          />
        </label>
        <label>
          Expires at (UTC, optional)
          <input
            type="datetime-local"
            value={expires}
            onInput={(e) => setExpires((e.target as HTMLInputElement).value)}
          />
        </label>
        {err && <div class="error">{err}</div>}
        <div class="row">
          <button type="button" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" disabled={busy}>
            {busy ? "..." : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
}
